import React, { useMemo, useRef, useState } from "react";
import BetterMeHelpModal from "./BetterMeHelpModal";
import BetterMeControlPanel from "./BetterMeControlPanel";
import {
  CURVE_SAMPLE_COUNT,
  HANDLE_DRAG_GAP_T,
  HANDLE_DUPLICATE_GUARD_T,
  HANDLE_EDGE_GUARD_T,
  buildDurationRegionEdit,
  buildEditedCurve,
  buildEditedCurveSamples,
  buildPitchPayload,
  clamp,
  cx,
  extractPrivateAudioId,
  getAudioFetchUrl,
  getCurveHzAtT,
  getCurvePointAtT,
  getPitchStats,
  getTFromTime,
  getTimeFromT,
  makeHandleId,
  makeHandlePoints,
  makeRegionId,
  makeSmoothPathByTime,
  makeTimelineX,
  normalizeBackendIntonationForEditor,
  normalizePitchSeries,
  pitchToSvgY,
  safeNumber,
  svgYToPitch,
  validateNewDurationRegion,
} from "./betterMeCurveUtils";

export default function CurveEditingBlock({
  apiBaseUrl,
  token,
  canInteract,
  canPlay,
  sourceAudioUrl,
  parentCommentId,
  meta,
  originalPitch,
  start = 0,
  end = 1,
  playbackSpeed = 1,
  audioRef,
  stopOtherAudio,
  onPreviewChange,
  onError,
  onSaveBetterMe,
}) {
  const width = 760;
  const height = 250;
  const edgePadX = 28;

  const svgRef = useRef(null);
  const originalPlayRef = useRef(null);
  const internalEditedPlayRef = useRef(null);
  const editedPlayRef = audioRef || internalEditedPlayRef;
  const playheadRafRef = useRef(null);
  const activePlaybackRef = useRef(null);

  const [helpOpen, setHelpOpen] = useState(false);

  const [dragHandleId, setDragHandleId] = useState(null);
  const [rangeDrag, setRangeDrag] = useState(null);
  const [hoverHandleId, setHoverHandleId] = useState(null);
  const [playheadT, setPlayheadT] = useState(null);

  const [preview, setPreview] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [savingBetterMe, setSavingBetterMe] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [localSpeed, setLocalSpeed] = useState(playbackSpeed || 1);

  const [rangeStartT, setRangeStartT] = useState(0);
  const [rangeEndT, setRangeEndT] = useState(1);

  const [durationFactor, setDurationFactor] = useState(1.25);
  const [durationRegions, setDurationRegions] = useState([]);
  const [durationWarning, setDurationWarning] = useState("");

  const [workingAudioUrl, setWorkingAudioUrl] = useState(null);
  const [workingPitch, setWorkingPitch] = useState(null);
  const [workingStart, setWorkingStart] = useState(start);
  const [workingEnd, setWorkingEnd] = useState(end);
  const [appliedTransformCount, setAppliedTransformCount] = useState(0);

  const immutableOriginalPoints = useMemo(
    () => normalizePitchSeries(originalPitch),
    [originalPitch]
  );

  const basePoints = useMemo(() => {
    if (Array.isArray(workingPitch) && workingPitch.length > 1) {
      return normalizePitchSeries(workingPitch);
    }

    return immutableOriginalPoints;
  }, [workingPitch, immutableOriginalPoints]);

  const originalDuration = Math.max(0.01, Number(end) - Number(start));
  const workingDuration = Math.max(
    0.01,
    Number(workingEnd) - Number(workingStart)
  );

  const maxTimelineDuration = Math.max(originalDuration, workingDuration);

  const pitchStats = useMemo(() => {
    return getPitchStats([...immutableOriginalPoints, ...basePoints]);
  }, [immutableOriginalPoints, basePoints]);

  const pitchFloor = pitchStats.minHz;
  const pitchCeiling = pitchStats.maxHz;

  const [handles, setHandles] = useState(() =>
    makeHandlePoints(immutableOriginalPoints, 9)
  );

  React.useEffect(() => {
    const initialPoints = normalizePitchSeries(originalPitch);
    const initialStats = getPitchStats(initialPoints);
    const nextHandles = makeHandlePoints(initialPoints, 9);
    const initialEditedPoints = buildEditedCurve(
      initialPoints,
      nextHandles,
      initialStats.minHz,
      initialStats.maxHz
    );

    const pitchEdit = buildPitchPayload({
      start,
      end,
      basePoints: initialPoints,
      editedPoints: initialEditedPoints,
      handles: nextHandles,
      pitchFloor: initialStats.minHz,
      pitchCeiling: initialStats.maxHz,
      rangeStartT: 0,
      rangeEndT: 1,
    });

    stopPlayback();

    setWorkingAudioUrl(null);
    setWorkingPitch(null);
    setWorkingStart(start);
    setWorkingEnd(end);
    setAppliedTransformCount(0);

    setHandles(nextHandles);
    setPreview(null);
    setDirty(false);
    setLoading(false);
    setSavingBetterMe(false);
    setPlaying(null);
    setDragHandleId(null);
    setHoverHandleId(null);
    setRangeDrag(null);
    setRangeStartT(0);
    setRangeEndT(1);
    setPlayheadT(null);
    setDurationFactor(1.25);
    setDurationRegions([]);
    setDurationWarning("");

    onPreviewChange?.({
      preview: null,
      dirty: false,
      edit: {
        type: "better_me_transform",
        mode: "pitch_and_duration",
        pitch: pitchEdit,
        durationRegions: [],
        edits: [pitchEdit],
      },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentCommentId, sourceAudioUrl, start, end, originalPitch]);

  React.useEffect(() => {
    return () => {
      stopPlayback();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editedPoints = useMemo(
    () => buildEditedCurve(basePoints, handles, pitchFloor, pitchCeiling),
    [basePoints, handles, pitchFloor, pitchCeiling]
  );

  // Visual-only curve: handles shape the pitch, but do not become path samples.
  // This prevents artificial violet bridges across unvoiced/devoiced gaps.
  const editedVisualPoints = useMemo(
    () => buildEditedCurveSamples(basePoints, handles, pitchFloor, pitchCeiling),
    [basePoints, handles, pitchFloor, pitchCeiling]
  );

  const originalPath = useMemo(
    () =>
      makeSmoothPathByTime({
        points: immutableOriginalPoints,
        curveStart: start,
        curveEnd: end,
        maxDuration: maxTimelineDuration,
        width,
        height,
        minHz: pitchFloor,
        maxHz: pitchCeiling,
        sampleCount: CURVE_SAMPLE_COUNT,
        voiceMaskPoints: immutableOriginalPoints,
        voiceMaskStart: start,
        voiceMaskEnd: end,
      }),
    [
      immutableOriginalPoints,
      start,
      end,
      maxTimelineDuration,
      pitchFloor,
      pitchCeiling,
    ]
  );

  const appliedEditedPath = useMemo(
    () =>
      makeSmoothPathByTime({
        points: basePoints,
        curveStart: workingStart,
        curveEnd: workingEnd,
        maxDuration: maxTimelineDuration,
        width,
        height,
        minHz: pitchFloor,
        maxHz: pitchCeiling,
        sampleCount: CURVE_SAMPLE_COUNT,
        voiceMaskPoints: basePoints,
        voiceMaskStart: workingStart,
        voiceMaskEnd: workingEnd,
      }),
    [
      basePoints,
      workingStart,
      workingEnd,
      maxTimelineDuration,
      pitchFloor,
      pitchCeiling,
    ]
  );

  const targetEditedPath = useMemo(
    () =>
      makeSmoothPathByTime({
        points: editedVisualPoints,
        curveStart: workingStart,
        curveEnd: workingEnd,
        maxDuration: maxTimelineDuration,
        width,
        height,
        minHz: pitchFloor,
        maxHz: pitchCeiling,
        sampleCount: CURVE_SAMPLE_COUNT,

        // Handles shape pitch, but the base curve defines where voice exists.
        // This prevents visual bridges across unvoiced/devoiced regions.
        voiceMaskPoints: basePoints,
        voiceMaskStart: workingStart,
        voiceMaskEnd: workingEnd,
      }),
    [
      editedVisualPoints,
      basePoints,
      workingStart,
      workingEnd,
      maxTimelineDuration,
      pitchFloor,
      pitchCeiling,
    ]
  );

  const hasOriginalPitch = immutableOriginalPoints.length > 0;
  const hasEditedAudio = Boolean(workingAudioUrl);
  const hasSavedTemp = Boolean(preview?.audio_id && preview?.audio_url);

  const pitchHasChanged = handles.some(
    (handle) => Math.abs(handle.hz - handle.originalHz) > 0.5
  );

  const hasUnsavedTarget = dirty || durationRegions.length > 0 || pitchHasChanged;

  const rangeStartTime = getTimeFromT(workingStart, workingEnd, rangeStartT);
  const rangeEndTime = getTimeFromT(workingStart, workingEnd, rangeEndT);

  const rangeStartRelative = rangeStartT * workingDuration;
  const rangeEndRelative = rangeEndT * workingDuration;

  const rangeStartX = makeTimelineX({
    relativeTime: rangeStartRelative,
    maxDuration: maxTimelineDuration,
    width,
  });

  const rangeEndX = makeTimelineX({
    relativeTime: rangeEndRelative,
    maxDuration: maxTimelineDuration,
    width,
  });

  const originalEndX = makeTimelineX({
    relativeTime: originalDuration,
    maxDuration: maxTimelineDuration,
    width,
  });

  const editedEndX = makeTimelineX({
    relativeTime: workingDuration,
    maxDuration: maxTimelineDuration,
    width,
  });

  const activePlaybackCurve =
    playing === "original" ? immutableOriginalPoints : basePoints;

  const activeCurveStart = playing === "original" ? start : workingStart;
  const activeCurveEnd = playing === "original" ? end : workingEnd;

  const playheadPoint =
    playheadT == null ? null : getCurvePointAtT(activePlaybackCurve, playheadT);

  const playheadRelativeTime =
    playheadT == null
      ? null
      : playheadT * (Number(activeCurveEnd) - Number(activeCurveStart));

  const playheadX =
    playheadRelativeTime == null
      ? null
      : makeTimelineX({
          relativeTime: playheadRelativeTime,
          maxDuration: maxTimelineDuration,
          width,
        });

  const playheadY =
    playheadPoint == null
      ? null
      : pitchToSvgY(playheadPoint.hz, height, pitchFloor, pitchCeiling);

  const getBaseHzAtT = (t) => {
    if (!basePoints.length) return pitchStats.centerHz;

    if (t <= basePoints[0].t) return basePoints[0].hz;

    if (t >= basePoints[basePoints.length - 1].t) {
      return basePoints[basePoints.length - 1].hz;
    }

    for (let i = 0; i < basePoints.length - 1; i += 1) {
      const a = basePoints[i];
      const b = basePoints[i + 1];

      if (t >= a.t && t <= b.t) {
        const span = b.t - a.t || 1;
        const local = (t - a.t) / span;

        return a.hz + (b.hz - a.hz) * local;
      }
    }

    return basePoints[basePoints.length - 1].hz;
  };

  const buildCurrentPitchPayload = (
    nextHandles = handles,
    nextRangeStartT = rangeStartT,
    nextRangeEndT = rangeEndT
  ) => {
    const nextEditedPoints = buildEditedCurve(
      basePoints,
      nextHandles,
      pitchFloor,
      pitchCeiling
    );

    return buildPitchPayload({
      start: workingStart,
      end: workingEnd,
      basePoints,
      editedPoints: nextEditedPoints,
      handles: nextHandles,
      pitchFloor,
      pitchCeiling,
      rangeStartT: nextRangeStartT,
      rangeEndT: nextRangeEndT,
    });
  };

  const buildCurrentTransformPayload = (
    nextHandles = handles,
    nextRangeStartT = rangeStartT,
    nextRangeEndT = rangeEndT,
    nextDurationRegions = durationRegions
  ) => {
    const pitchEdit = buildCurrentPitchPayload(
      nextHandles,
      nextRangeStartT,
      nextRangeEndT
    );

    return {
      type: "better_me_transform",
      mode: "pitch_and_duration",
      pitch: pitchEdit,
      durationRegions: nextDurationRegions,
      edits: [pitchEdit, ...nextDurationRegions],
    };
  };

  const emitEditChange = (
    nextHandles,
    nextPreview = preview,
    nextRangeStartT = rangeStartT,
    nextRangeEndT = rangeEndT,
    nextDurationRegions = durationRegions
  ) => {
    const edit = buildCurrentTransformPayload(
      nextHandles,
      nextRangeStartT,
      nextRangeEndT,
      nextDurationRegions
    );

    if (nextPreview) {
      setDirty(true);
      onPreviewChange?.({
        preview: nextPreview,
        dirty: true,
        edit,
      });
    } else {
      onPreviewChange?.({
        preview: null,
        dirty: false,
        edit,
      });
    }
  };

  const normalizeHandle = (handle) => {
    const t = clamp(safeNumber(handle.t), 0, 1);
    const originalHz = clamp(
      safeNumber(handle.originalHz, handle.hz),
      pitchFloor,
      pitchCeiling
    );
    const hz = clamp(
      safeNumber(handle.hz, originalHz),
      pitchFloor,
      pitchCeiling
    );

    return {
      ...handle,
      id: handle.id || makeHandleId(),
      t,
      originalHz,
      hz,
      time: getTimeFromT(workingStart, workingEnd, t),
    };
  };

  const sortHandles = (items) => {
    return [...items].map(normalizeHandle).sort((a, b) => a.t - b.t);
  };

  const protectEndpointHandles = (items) => {
    const sorted = sortHandles(items);

    if (!sorted.length) return sorted;

    sorted[0] = {
      ...sorted[0],
      t: 0,
      originalHz: getBaseHzAtT(0),
      time: getTimeFromT(workingStart, workingEnd, 0),
    };

    sorted[sorted.length - 1] = {
      ...sorted[sorted.length - 1],
      t: 1,
      originalHz: getBaseHzAtT(1),
      time: getTimeFromT(workingStart, workingEnd, 1),
    };

    return sorted;
  };

  const cleanAndSetHandles = (nextHandles) => {
    const cleaned = protectEndpointHandles(nextHandles);

    setHandles(cleaned);
    emitEditChange(cleaned);
  };

  const getEditedHzAtT = (t) => {
    const fromEdited = getCurveHzAtT(editedPoints, t);

    if (Number.isFinite(Number(fromEdited))) {
      return Number(fromEdited);
    }

    const fromBase = getBaseHzAtT(t);

    if (Number.isFinite(Number(fromBase))) {
      return Number(fromBase);
    }

    return pitchStats.centerHz;
  };

  const resetToOriginal = () => {
    stopPlayback();

    const nextHandles = makeHandlePoints(immutableOriginalPoints, 9);
    const initialStats = getPitchStats(immutableOriginalPoints);
    const initialEditedPoints = buildEditedCurve(
      immutableOriginalPoints,
      nextHandles,
      initialStats.minHz,
      initialStats.maxHz
    );

    const pitchEdit = buildPitchPayload({
      start,
      end,
      basePoints: immutableOriginalPoints,
      editedPoints: initialEditedPoints,
      handles: nextHandles,
      pitchFloor: initialStats.minHz,
      pitchCeiling: initialStats.maxHz,
      rangeStartT: 0,
      rangeEndT: 1,
    });

    setWorkingAudioUrl(null);
    setWorkingPitch(null);
    setWorkingStart(start);
    setWorkingEnd(end);
    setAppliedTransformCount(0);

    setHandles(nextHandles);
    setPreview(null);
    setDirty(false);
    setLoading(false);
    setSavingBetterMe(false);
    setDragHandleId(null);
    setHoverHandleId(null);
    setRangeDrag(null);
    setRangeStartT(0);
    setRangeEndT(1);
    setPlayheadT(null);
    setDurationFactor(1.25);
    setDurationRegions([]);
    setDurationWarning("");

    onPreviewChange?.({
      preview: null,
      dirty: false,
      edit: {
        type: "better_me_transform",
        mode: "pitch_and_duration",
        pitch: pitchEdit,
        durationRegions: [],
        edits: [pitchEdit],
      },
    });
  };

  const getPointerClientPoint = (event) => {
    const source =
      event.touches?.[0] ||
      event.changedTouches?.[0] ||
      event.nativeEvent?.touches?.[0] ||
      event.nativeEvent?.changedTouches?.[0] ||
      event;

    return {
      clientX: source.clientX,
      clientY: source.clientY,
    };
  };

  const svgToPitchPoint = (event) => {
    if (!svgRef.current) {
      return {
        t: 0,
        hz: pitchStats.centerHz,
        relativeTime: 0,
        svgX: 0,
        svgY: 0,
      };
    }

    const { clientX, clientY } = getPointerClientPoint(event);

    let svgX = 0;
    let svgY = 0;

    // Use real SVG coordinates, not DOM rect ratios.
    // Rect-ratio mapping drifts when the viewBox is scaled or letterboxed,
    // which can make new nodes appear left of the click near the right edge.
    try {
      const point = svgRef.current.createSVGPoint();
      point.x = clientX;
      point.y = clientY;

      const ctm = svgRef.current.getScreenCTM();

      if (ctm) {
        const svgPoint = point.matrixTransform(ctm.inverse());
        svgX = svgPoint.x;
        svgY = svgPoint.y;
      } else {
        throw new Error("Missing SVG CTM");
      }
    } catch {
      const rect = svgRef.current.getBoundingClientRect();
      const xRatio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
      const yRatio = rect.height > 0 ? (clientY - rect.top) / rect.height : 0;

      svgX = xRatio * width;
      svgY = yRatio * height;
    }

    svgX = clamp(svgX, 0, width);
    svgY = clamp(svgY, 0, height);

    const relativeTime = (svgX / width) * maxTimelineDuration;
    const t = clamp(relativeTime / workingDuration, 0, 1);
    const hz = svgYToPitch(svgY, height, pitchFloor, pitchCeiling);

    return {
      t,
      hz: clamp(hz, pitchFloor, pitchCeiling),
      relativeTime,
      svgX,
      svgY,
    };
  };

  const handlePointerMove = (event) => {
    if (!canInteract) return;

    if (rangeDrag) {
      event.preventDefault();

      const next = svgToPitchPoint(event).t;
      const minSpan = 0.03;

      if (rangeDrag === "start") {
        const nextStart = clamp(next, 0, rangeEndT - minSpan);
        setRangeStartT(nextStart);
        setPlayheadT(nextStart);
        emitEditChange(handles, preview, nextStart, rangeEndT);
      }

      if (rangeDrag === "end") {
        const nextEnd = clamp(next, rangeStartT + minSpan, 1);
        setRangeEndT(nextEnd);
        setPlayheadT(nextEnd);
        emitEditChange(handles, preview, rangeStartT, nextEnd);
      }

      return;
    }

    if (dragHandleId === null) return;

    event.preventDefault();

    const nextPoint = svgToPitchPoint(event);
    const sortedHandles = sortHandles(handles);
    const sortedIndex = sortedHandles.findIndex(
      (handle) => handle.id === dragHandleId
    );

    if (sortedIndex === -1) return;

    const nextHandles = sortedHandles.map((handle, index) => {
      if (handle.id !== dragHandleId) return handle;

      if (index === 0) {
        return {
          ...handle,
          hz: nextPoint.hz,
          t: 0,
          originalHz: getBaseHzAtT(0),
          time: getTimeFromT(workingStart, workingEnd, 0),
        };
      }

      if (index === sortedHandles.length - 1) {
        return {
          ...handle,
          hz: nextPoint.hz,
          t: 1,
          originalHz: getBaseHzAtT(1),
          time: getTimeFromT(workingStart, workingEnd, 1),
        };
      }

      const prevT = sortedHandles[index - 1]?.t ?? 0;
      const nextT = sortedHandles[index + 1]?.t ?? 1;

      const nextTClamped = clamp(
        nextPoint.t,
        prevT + HANDLE_DRAG_GAP_T,
        nextT - HANDLE_DRAG_GAP_T
      );

      return {
        ...handle,
        t: nextTClamped,
        hz: nextPoint.hz,
        originalHz: getBaseHzAtT(nextTClamped),
        time: getTimeFromT(workingStart, workingEnd, nextTClamped),
      };
    });

    cleanAndSetHandles(nextHandles);
  };

  const releasePointer = (event) => {
    if (event?.currentTarget?.releasePointerCapture && event.pointerId != null) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch {
        // no-op
      }
    }

    setDragHandleId(null);
    setRangeDrag(null);
  };

  const addPoint = (event) => {
    if (!canInteract) return;

    event.preventDefault();
    event.stopPropagation();

    const clicked = svgToPitchPoint(event);

    // Exact clicked x-position in SVG coordinates. Only protect the two endpoint anchors.
    const t = clamp(
      clicked.t,
      HANDLE_EDGE_GUARD_T,
      1 - HANDLE_EDGE_GUARD_T
    );

    const sorted = sortHandles(handles);

    const tooClose = sorted.some(
      (handle) =>
        Math.abs(Number(handle.t) - t) < HANDLE_DUPLICATE_GUARD_T
    );

    if (tooClose) return;

    const originalHz = getBaseHzAtT(t);
    const currentEditedHz = getEditedHzAtT(t);

    const nextHandle = {
      id: makeHandleId(),
      t,
      originalHz,
      hz: currentEditedHz,
      time: getTimeFromT(workingStart, workingEnd, t),
    };

    const nextHandles = protectEndpointHandles([...sorted, nextHandle]);

    setHandles(nextHandles);
    emitEditChange(nextHandles);
  };

  const removePoint = (indexToRemove) => {
    if (!canInteract) return;
    if (handles.length <= 2) return;
    if (indexToRemove === 0 || indexToRemove === handles.length - 1) return;

    cleanAndSetHandles(handles.filter((_, index) => index !== indexToRemove));
  };

  const addDurationRegion = () => {
    if (!canInteract) return;

    const factor = Number(durationFactor);

    if (!Number.isFinite(factor) || factor === 1) {
      setDurationWarning("Choose a duration factor other than 1×.");
      return;
    }

    const nextRegion = buildDurationRegionEdit({
      id: makeRegionId(),
      start: workingStart,
      end: workingEnd,
      rangeStartT,
      rangeEndT,
      factor,
      ramp: 0.03,
    });

    const validation = validateNewDurationRegion(nextRegion, durationRegions);

    if (!validation.ok) {
      setDurationWarning(validation.message);
      return;
    }

    const nextRegions = [...durationRegions, nextRegion];

    setDurationRegions(nextRegions);
    setDurationWarning("");

    const edit = buildCurrentTransformPayload(
      handles,
      rangeStartT,
      rangeEndT,
      nextRegions
    );

    if (preview) {
      setDirty(true);
      onPreviewChange?.({
        preview,
        dirty: true,
        edit,
      });
    } else {
      onPreviewChange?.({
        preview: null,
        dirty: false,
        edit,
      });
    }
  };

  const removeDurationRegion = (regionId) => {
    const nextRegions = durationRegions.filter(
      (region) => region.id !== regionId
    );

    setDurationRegions(nextRegions);
    setDurationWarning("");

    const edit = buildCurrentTransformPayload(
      handles,
      rangeStartT,
      rangeEndT,
      nextRegions
    );

    if (preview) {
      setDirty(true);
      onPreviewChange?.({
        preview,
        dirty: true,
        edit,
      });
    } else {
      onPreviewChange?.({
        preview: null,
        dirty: false,
        edit,
      });
    }
  };

  const stopPlayheadAnimation = () => {
    if (playheadRafRef.current) {
      cancelAnimationFrame(playheadRafRef.current);
      playheadRafRef.current = null;
    }

    activePlaybackRef.current = null;
    setPlayheadT(null);
  };

  const startPlayheadAnimation = ({
    player,
    kind,
    clipStart,
    clipEnd,
    curveStart,
    curveEnd,
  }) => {
    stopPlayheadAnimation();

    activePlaybackRef.current = {
      player,
      kind,
      clipStart,
      clipEnd,
      curveStart,
      curveEnd,
    };

    const tick = () => {
      const active = activePlaybackRef.current;

      if (!active?.player) {
        stopPlayheadAnimation();
        return;
      }

      const current = Number(active.player.currentTime || 0);
      const nextT = getTFromTime(active.curveStart, active.curveEnd, current);

      setPlayheadT(nextT);

      if (
        current >= active.clipEnd ||
        active.player.ended ||
        active.player.paused
      ) {
        if (current >= active.clipEnd) {
          active.player.pause();
          active.player.currentTime = active.clipStart;
        }

        if (active.player.src?.startsWith("blob:")) {
          URL.revokeObjectURL(active.player.src);
        }

        if (active.kind === "edited") {
          editedPlayRef.current = null;
        } else {
          originalPlayRef.current = null;
        }

        setPlaying(null);
        stopPlayheadAnimation();
        return;
      }

      playheadRafRef.current = requestAnimationFrame(tick);
    };

    playheadRafRef.current = requestAnimationFrame(tick);
  };

  function stopPlayback() {
    stopPlayheadAnimation();

    if (originalPlayRef.current) {
      originalPlayRef.current.pause();

      if (originalPlayRef.current.src?.startsWith("blob:")) {
        URL.revokeObjectURL(originalPlayRef.current.src);
      }

      originalPlayRef.current = null;
    }

    if (editedPlayRef?.current) {
      editedPlayRef.current.pause();

      if (editedPlayRef.current.src?.startsWith("blob:")) {
        URL.revokeObjectURL(editedPlayRef.current.src);
      }

      editedPlayRef.current = null;
    }

    setPlaying(null);
    setPlayheadT(null);
  }

  const handleSpeedChange = (speed) => {
    setLocalSpeed(speed);

    if (originalPlayRef.current) {
      originalPlayRef.current.playbackRate = speed;
    }

    if (editedPlayRef?.current) {
      editedPlayRef.current.playbackRate = speed;
    }
  };

  const playBetweenLines = async (kind) => {
    if (playing === kind) {
      stopPlayback();
      return;
    }

    const audioUrl = kind === "original" ? sourceAudioUrl : workingAudioUrl;

    if (!audioUrl || !canPlay) return;

    const fetchUrl = getAudioFetchUrl(apiBaseUrl, audioUrl);

    if (!fetchUrl) return;

    const curveStart = kind === "original" ? start : workingStart;
    const curveEnd = kind === "original" ? end : workingEnd;

    const clipStart = Math.max(
      0,
      getTimeFromT(curveStart, curveEnd, rangeStartT)
    );
    const clipEnd = Math.max(
      clipStart + 0.03,
      getTimeFromT(curveStart, curveEnd, rangeEndT)
    );

    setLoading(true);
    onError?.("");

    try {
      stopOtherAudio?.();
      stopPlayback();

      const res = await fetch(fetchUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (res.status === 401 || res.status === 403) {
        throw new Error("LOGIN_REQUIRED");
      }

      if (!res.ok) {
        throw new Error(`${kind} audio ${res.status}`);
      }

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const player = new Audio(objectUrl);

      player.playbackRate = localSpeed;

      if (kind === "edited") {
        editedPlayRef.current = player;
      } else {
        originalPlayRef.current = player;
      }

      player.onloadedmetadata = () => {
        player.currentTime = Math.min(
          clipStart,
          Math.max(0, player.duration || clipStart)
        );
      };

      player.onplay = () => {
        setPlaying(kind);

        startPlayheadAnimation({
          player,
          kind,
          clipStart,
          clipEnd,
          curveStart,
          curveEnd,
        });
      };

      player.onerror = () => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }

        if (kind === "edited") {
          editedPlayRef.current = null;
        } else {
          originalPlayRef.current = null;
        }

        stopPlayheadAnimation();
        setPlaying(null);
        onError?.("Could not play audio.");
      };

      await new Promise((resolve) => {
        if (player.readyState >= 1) {
          resolve();
          return;
        }

        player.addEventListener("loadedmetadata", resolve, { once: true });
      });

      player.currentTime = clipStart;
      setPlayheadT(getTFromTime(curveStart, curveEnd, clipStart));
      await player.play();
    } catch (error) {
      console.error("play between lines failed:", error);

      setPlaying(null);
      stopPlayheadAnimation();

      onError?.(
        error.message === "LOGIN_REQUIRED"
          ? "Log in to listen."
          : "Could not play selected audio."
      );
    } finally {
      setLoading(false);
    }
  };

  const commitBackendPreviewAsWorkingBase = (data, transform) => {
    const nextBase = normalizeBackendIntonationForEditor(data?.intonation);

    setPreview(data);
    setDirty(false);

    if (!nextBase.points.length) {
      onPreviewChange?.({
        preview: data,
        dirty: false,
        edit: transform,
      });

      return;
    }

    const nextStats = getPitchStats(nextBase.points);
    const nextHandles = makeHandlePoints(nextBase.points, 9);
    const nextEditedPoints = buildEditedCurve(
      nextBase.points,
      nextHandles,
      nextStats.minHz,
      nextStats.maxHz
    );

    const nextPitchEdit = buildPitchPayload({
      start: nextBase.start,
      end: nextBase.end,
      basePoints: nextBase.points,
      editedPoints: nextEditedPoints,
      handles: nextHandles,
      pitchFloor: nextStats.minHz,
      pitchCeiling: nextStats.maxHz,
      rangeStartT: 0,
      rangeEndT: 1,
    });

    setWorkingAudioUrl(data.audio_url);
    setWorkingPitch(nextBase.points);
    setWorkingStart(nextBase.start);
    setWorkingEnd(nextBase.end);
    setAppliedTransformCount((count) => count + 1);

    setHandles(nextHandles);
    setRangeStartT(0);
    setRangeEndT(1);
    setPlayheadT(null);
    setDurationRegions([]);
    setDurationFactor(1.25);
    setDurationWarning("");

    onPreviewChange?.({
      preview: data,
      dirty: false,
      edit: {
        type: "better_me_transform",
        mode: "pitch_and_duration",
        pitch: nextPitchEdit,
        durationRegions: [],
        edits: [nextPitchEdit],
        previousTransform: transform,
      },
    });
  };

  // Generates an edited audio preview from the current pitch/duration edits.
  // The generated preview becomes the new working base so edits can be chained.
  const tryPreview = async () => {
    if (!canInteract || !token) {
      onError?.("Log in to generate Better Me preview.");
      return;
    }

    const currentSourceAudioUrl = workingAudioUrl || sourceAudioUrl;
    const sourceAudioId = extractPrivateAudioId(currentSourceAudioUrl);

    if (!sourceAudioId) {
      onError?.("This recording is not private audio and cannot be transformed yet.");
      return;
    }

    const transform = buildCurrentTransformPayload();

    setLoading(true);
    onError?.("");

    try {
      stopOtherAudio?.();
      stopPlayback();

      const res = await fetch(
        `${apiBaseUrl}/audio/private/${sourceAudioId}/better-me-temp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            parentCommentId,
            mode: "better_me_transform",
            edits: transform.edits,
            meta: {
              ...meta,
              transform,
              sourceMode: workingAudioUrl ? "edited" : "original",
            },
          }),
        }
      );

      if (res.status === 401 || res.status === 403) {
        throw new Error("LOGIN_REQUIRED");
      }

      if (!res.ok) {
        throw new Error(`Better Me ${res.status}`);
      }

      const data = await res.json();

      commitBackendPreviewAsWorkingBase(data, transform);
    } catch (error) {
      console.error("create Better Me preview failed:", error);

      onError?.(
        error.message === "LOGIN_REQUIRED"
          ? "Log in to generate Better Me preview."
          : "Could not generate Better Me preview."
      );
    } finally {
      setLoading(false);
    }
  };

  const saveBetterMe = async () => {
    if (!preview?.audio_id || !preview?.audio_url) {
      onError?.("Generate Better Me first.");
      return;
    }

    setSavingBetterMe(true);
    onError?.("");

    try {
      stopPlayback();

      const accepted = {
        audio_id: preview.audio_id,
        audio_url: preview.audio_url,
        source_audio_id: preview.source_audio_id,
        kind: "better_me_final",
        intonation: preview.intonation,
        workingStart,
        workingEnd,
        transform: buildCurrentTransformPayload(),
      };

      await onSaveBetterMe?.(accepted);
    } catch (error) {
      console.error("save Better Me failed:", error);
      onError?.("Could not save Better Me.");
    } finally {
      setSavingBetterMe(false);
    }
  };

  const hintText =
    playing === "original"
      ? "Playing selected A/B region from the original recording."
      : playing === "edited"
        ? "Playing selected A/B region from the edited Better Me audio."
        : dirty
          ? "Edits changed. Generate a new Better Me preview."
          : workingAudioUrl
            ? "Preview ready. Compare the selected A/B region, continue editing, or save it."
            : "Shape the melody, choose A/B if needed, then generate a Better Me audio preview.";

  return (
    <div className="rounded-[28px] border border-violet-200 bg-gradient-to-br from-white via-violet-50/30 to-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm font-black text-slate-950">
              Edit Better Me curve
            </div>

            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-600">
              Original kept
            </span>

            {hasEditedAudio && (
              <span className="rounded-full border border-violet-200 bg-violet-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-violet-800">
                Edited v{appliedTransformCount}
              </span>
            )}

            {hasEditedAudio && workingDuration > originalDuration + 0.02 && (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-amber-800">
                +{(workingDuration - originalDuration).toFixed(2)}s longer
              </span>
            )}

            {hasEditedAudio && workingDuration < originalDuration - 0.02 && (
              <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-blue-800">
                −{(originalDuration - workingDuration).toFixed(2)}s shorter
              </span>
            )}

            {dirty && (
              <span className="rounded-full border border-amber-200 bg-amber-100 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-amber-800">
                Needs preview
              </span>
            )}

            {hasSavedTemp && (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-800">
                Preview ready
              </span>
            )}
          </div>

          <div className="mt-1 text-xs font-semibold text-slate-500">
            Generate Better Me creates an edited audio preview. Save Better Me attaches the latest preview to your verdict.
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => setHelpOpen(true)}
            title="How Better Me editing works"
            className="rounded-2xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-black text-violet-800 shadow-sm transition hover:-translate-y-0.5 hover:border-violet-300 hover:bg-white"
          >
            Help
          </button>

          <button
            type="button"
            onClick={resetToOriginal}
            disabled={!canInteract || loading || savingBetterMe}
            title="Reset curve, duration regions, and generated preview"
            className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-black text-slate-600 shadow-sm transition hover:-translate-y-0.5 hover:border-violet-200 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Reset all
          </button>
        </div>
      </div>

      {!hasOriginalPitch && (
        <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-800">
          No pitch data was found. The editor is showing a fallback sketch.
        </div>
      )}

      <div className="mt-4 overflow-hidden rounded-[26px] border border-slate-200 bg-white shadow-inner">
        <svg
          ref={svgRef}
          viewBox={`${-edgePadX} 0 ${width + edgePadX * 2} ${height}`}
          preserveAspectRatio="none"
          className={cx(
            "h-[250px] w-full touch-none select-none",
            !canInteract && "opacity-60"
          )}
          onDoubleClick={addPoint}
          onPointerMove={handlePointerMove}
          onPointerUp={releasePointer}
          onPointerCancel={releasePointer}
          onPointerLeave={releasePointer}
          onContextMenu={(event) => event.preventDefault()}
        >
          <defs>
            <linearGradient id="betterMeTargetGradient" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" stopColor="#7c3aed" />
              <stop offset="55%" stopColor="#8b5cf6" />
              <stop offset="100%" stopColor="#6366f1" />
            </linearGradient>

            <filter id="betterMeSoftGlow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.2" result="blur" />
              <feColorMatrix
                in="blur"
                type="matrix"
                values="
                  0 0 0 0 0.486
                  0 0 0 0 0.227
                  0 0 0 0 0.929
                  0 0 0 0.25 0
                "
                result="glow"
              />
              <feMerge>
                <feMergeNode in="glow" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <rect
            x={-edgePadX}
            y="0"
            width={width + edgePadX * 2}
            height={height}
            fill="white"
          />

          <rect
            x={-edgePadX}
            y="0"
            width={edgePadX}
            height={height}
            fill="rgba(248, 250, 252, 0.85)"
            pointerEvents="none"
          />

          <rect
            x={width}
            y="0"
            width={edgePadX}
            height={height}
            fill="rgba(248, 250, 252, 0.85)"
            pointerEvents="none"
          />

          <rect
            x={rangeStartX}
            y="0"
            width={Math.max(1, rangeEndX - rangeStartX)}
            height={height}
            fill="rgba(124, 58, 237, 0.06)"
            pointerEvents="none"
          />

          {durationRegions.map((region) => {
            const x1 = makeTimelineX({
              relativeTime: region.startT * workingDuration,
              maxDuration: maxTimelineDuration,
              width,
            });

            const x2 = makeTimelineX({
              relativeTime: region.endT * workingDuration,
              maxDuration: maxTimelineDuration,
              width,
            });

            const regionWidth = Math.max(2, x2 - x1);
            const isStretch = region.factor > 1;

            return (
              <g key={region.id} pointerEvents="none">
                <rect
                  x={x1}
                  y="0"
                  width={regionWidth}
                  height={height}
                  fill={
                    isStretch
                      ? "rgba(245, 158, 11, 0.13)"
                      : "rgba(59, 130, 246, 0.11)"
                  }
                />

                <line
                  x1={x1}
                  x2={x1}
                  y1="0"
                  y2={height}
                  stroke={isStretch ? "#f59e0b" : "#3b82f6"}
                  strokeWidth="1.5"
                  strokeDasharray="5 5"
                />

                <line
                  x1={x2}
                  x2={x2}
                  y1="0"
                  y2={height}
                  stroke={isStretch ? "#f59e0b" : "#3b82f6"}
                  strokeWidth="1.5"
                  strokeDasharray="5 5"
                />

                <rect
                  x={Math.min(x1 + 6, width - 58)}
                  y={height - 30}
                  width="52"
                  height="22"
                  rx="11"
                  fill="white"
                  stroke={isStretch ? "#f59e0b" : "#3b82f6"}
                  strokeWidth="1.25"
                />

                <text
                  x={Math.min(x1 + 32, width - 32)}
                  y={height - 15}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="900"
                  fill={isStretch ? "#b45309" : "#1d4ed8"}
                >
                  {region.factor}×
                </text>
              </g>
            );
          })}

          {[0, 0.25, 0.5, 0.75, 1].map((x) => (
            <line
              key={`v-${x}`}
              x1={x * width}
              x2={x * width}
              y1="0"
              y2={height}
              stroke="#f1f5f9"
              strokeWidth="1"
            />
          ))}

          {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
            const hz = pitchFloor + ratio * (pitchCeiling - pitchFloor);
            const y = pitchToSvgY(hz, height, pitchFloor, pitchCeiling);

            return (
              <g key={`h-${ratio}`}>
                <line
                  x1={-edgePadX}
                  x2={width + edgePadX}
                  y1={y}
                  y2={y}
                  stroke={ratio === 0.5 ? "#cbd5e1" : "#f1f5f9"}
                  strokeWidth={ratio === 0.5 ? 2 : 1}
                />

                <text
                  x="12"
                  y={y - 6}
                  fontSize="11"
                  fontWeight="800"
                  fill="#64748b"
                >
                  {Math.round(hz)} Hz
                </text>
              </g>
            );
          })}

          <line
            x1={originalEndX}
            x2={originalEndX}
            y1="0"
            y2={height}
            stroke="#2563eb"
            strokeWidth="1.5"
            strokeDasharray="4 5"
            opacity="0.45"
          />

          {hasEditedAudio && (
            <line
              x1={editedEndX}
              x2={editedEndX}
              y1="0"
              y2={height}
              stroke="#7c3aed"
              strokeWidth="1.8"
              strokeDasharray="4 5"
              opacity="0.65"
            />
          )}

          {originalPath && (
            <path
              d={originalPath}
              fill="none"
              stroke="#2563eb"
              strokeWidth={playing === "original" ? "5.25" : "3.9"}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={playing === "edited" ? 0.28 : hasEditedAudio ? 0.62 : 0.86}
            />
          )}

          {hasEditedAudio && appliedEditedPath && (
            <path
              d={appliedEditedPath}
              fill="none"
              stroke={playing === "edited" ? "#7c3aed" : "#a78bfa"}
              strokeWidth={playing === "edited" ? "5.25" : "3.75"}
              strokeLinecap="round"
              strokeLinejoin="round"
              opacity={playing === "original" ? 0.35 : 0.95}
              filter={playing === "edited" ? "url(#betterMeSoftGlow)" : undefined}
            />
          )}

          {targetEditedPath && hasUnsavedTarget && (
            <path
              d={targetEditedPath}
              fill="none"
              stroke="url(#betterMeTargetGradient)"
              strokeWidth="4.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray={hasEditedAudio ? "8 6" : undefined}
              opacity={playing ? 0.45 : 1}
              filter={!playing ? "url(#betterMeSoftGlow)" : undefined}
            />
          )}

          {playheadX != null && playheadY != null && (
            <g pointerEvents="none">
              <line
                x1={playheadX}
                x2={playheadX}
                y1="0"
                y2={height}
                stroke="rgba(255,255,255,0.95)"
                strokeWidth="7"
                strokeLinecap="round"
              />

              <line
                x1={playheadX}
                x2={playheadX}
                y1="0"
                y2={height}
                stroke={playing === "original" ? "#2563eb" : "#10b981"}
                strokeWidth="3.25"
                strokeLinecap="round"
              />

              <circle
                cx={playheadX}
                cy={playheadY}
                r="15"
                fill={
                  playing === "original"
                    ? "rgba(37,99,235,0.16)"
                    : "rgba(16,185,129,0.16)"
                }
                stroke={
                  playing === "original"
                    ? "rgba(37,99,235,0.3)"
                    : "rgba(16,185,129,0.3)"
                }
                strokeWidth="2"
              />

              <circle
                cx={playheadX}
                cy={playheadY}
                r="9"
                fill={playing === "original" ? "#2563eb" : "#10b981"}
                stroke="white"
                strokeWidth="3"
              />
            </g>
          )}

          {[
            { key: "start", x: rangeStartX, label: "A" },
            { key: "end", x: rangeEndX, label: "B" },
          ].map((line) => (
            <g key={line.key}>
              <rect
                x={line.x - 16}
                y="0"
                width="32"
                height={height}
                fill="transparent"
                className="cursor-ew-resize"
                onPointerDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();

                  try {
                    event.currentTarget.setPointerCapture(event.pointerId);
                  } catch {
                    // no-op
                  }

                  setRangeDrag(line.key);
                  setPlayheadT(line.key === "start" ? rangeStartT : rangeEndT);
                }}
                onDoubleClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
              />

              <line
                x1={line.x}
                x2={line.x}
                y1="0"
                y2={height}
                stroke="#f97316"
                strokeWidth="2.75"
                strokeLinecap="round"
                pointerEvents="none"
              />

              <rect
                x={line.x - 14}
                y="8"
                width="28"
                height="20"
                rx="10"
                fill="#fff7ed"
                stroke="#fb923c"
                strokeWidth="1.5"
                pointerEvents="none"
              />

              <text
                x={line.x}
                y="22"
                textAnchor="middle"
                fontSize="11"
                fontWeight="900"
                fill="#c2410c"
                pointerEvents="none"
              >
                {line.label}
              </text>
            </g>
          ))}

          {handles.map((handle, index) => {
            const relativeTime = handle.t * workingDuration;
            const svgPoint = {
              x: makeTimelineX({
                relativeTime,
                maxDuration: maxTimelineDuration,
                width,
              }),
              y: pitchToSvgY(handle.hz, height, pitchFloor, pitchCeiling),
            };

            const originalY = pitchToSvgY(
              handle.originalHz,
              height,
              pitchFloor,
              pitchCeiling
            );

            const delta = handle.hz - handle.originalHz;
            const active = dragHandleId === handle.id || hoverHandleId === handle.id;
            const removable = index !== 0 && index !== handles.length - 1;

            return (
              <g
                key={handle.id || `${index}-${handle.t}-${handle.hz}`}
                opacity={playing ? 0.45 : 1}
              >
                <line
                  x1={svgPoint.x}
                  x2={svgPoint.x}
                  y1={originalY}
                  y2={svgPoint.y}
                  stroke="#a78bfa"
                  strokeWidth={active ? 2.5 : 1.75}
                  strokeDasharray="4 4"
                  opacity={Math.abs(delta) > 0.5 ? 1 : 0.35}
                  pointerEvents="none"
                />

                <circle
                  cx={svgPoint.x}
                  cy={svgPoint.y}
                  r={active ? 16 : 14}
                  fill="rgba(124,58,237,0.10)"
                  stroke="rgba(124,58,237,0.18)"
                  strokeWidth="2"
                  pointerEvents="none"
                />

                <circle
                  cx={svgPoint.x}
                  cy={svgPoint.y}
                  r={active ? 11 : 9.5}
                  fill="white"
                  stroke="#7c3aed"
                  strokeWidth={active ? 4.5 : 3.5}
                  className="cursor-grab active:cursor-grabbing"
                  onPointerEnter={() => setHoverHandleId(handle.id)}
                  onPointerLeave={() => setHoverHandleId(null)}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (event.altKey || event.button === 2) {
                      removePoint(index);
                      return;
                    }

                    try {
                      event.currentTarget.setPointerCapture(event.pointerId);
                    } catch {
                      // no-op
                    }

                    setDragHandleId(handle.id);
                  }}
                  onDoubleClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                  }}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    if (removable) {
                      removePoint(index);
                    }
                  }}
                />

                <text
                  x={svgPoint.x + 14}
                  y={svgPoint.y - 14}
                  fontSize="11"
                  fontWeight="900"
                  fill={Math.abs(delta) > 0.5 ? "#5b21b6" : "#8b5cf6"}
                  pointerEvents="none"
                >
                  {delta >= 0 ? "+" : ""}
                  {Math.round(delta)}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="mt-3 flex flex-col gap-2 text-xs font-semibold text-slate-500 sm:flex-row sm:items-center sm:justify-between">
        <span>
          Drag purple nodes to shape pitch. Orange A/B lines choose the playable region. Blue = original, violet = edited, dashed violet = next preview.
        </span>

        <div className="flex flex-wrap gap-2">
          <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 font-black text-blue-700 shadow-sm">
            Original {originalDuration.toFixed(2)}s
          </span>

          <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 font-black text-violet-700 shadow-sm">
            Edited {workingDuration.toFixed(2)}s
          </span>

          <span className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 font-black text-orange-700 shadow-sm">
            A/B {rangeStartTime.toFixed(2)}–{rangeEndTime.toFixed(2)}s
          </span>
        </div>
      </div>

      {durationRegions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {durationRegions.map((region, index) => (
            <button
              key={region.id}
              type="button"
              onClick={() => removeDurationRegion(region.id)}
              className={cx(
                "rounded-full border px-3 py-1.5 text-xs font-black shadow-sm transition hover:-translate-y-0.5",
                region.factor > 1
                  ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                  : "border-blue-300 bg-blue-50 text-blue-800 hover:bg-blue-100"
              )}
              title="Click to remove this duration edit"
            >
              {index + 1}. {region.startTime.toFixed(2)}–
              {region.endTime.toFixed(2)}s · {region.factor}× ×
            </button>
          ))}
        </div>
      )}

      {durationWarning && (
        <div className="mt-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-900 shadow-sm">
          {durationWarning}
        </div>
      )}

      <BetterMeControlPanel
        hintText={hintText}
        durationFactor={durationFactor}
        onDurationFactorChange={setDurationFactor}
        onAddDurationRegion={addDurationRegion}
        localSpeed={localSpeed}
        onSpeedChange={handleSpeedChange}
        playing={playing}
        canInteract={canInteract}
        canPlay={canPlay}
        loading={loading}
        savingBetterMe={savingBetterMe}
        sourceAudioUrl={sourceAudioUrl}
        workingAudioUrl={workingAudioUrl}
        previewAudioId={preview?.audio_id}
        handlesCount={handles.length}
        hasUnsavedTarget={hasUnsavedTarget}
        onPlayOriginal={() => playBetweenLines("original")}
        onPlayEdited={() => playBetweenLines("edited")}
        onTryPreview={tryPreview}
        onSaveBetterMe={saveBetterMe}
      />

      <BetterMeHelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
      />
    </div>
  );
}